import { useEffect, useMemo, useRef, useState } from "react"
import { getAuditsByReferenceIdAndType } from "routes/audit/controller/get-audits-by-reference-id"
import { invalidate } from "lib/graphql/cache"
import { PAYLOAD_STATUS_TASK_REMOVAL_UNDONE, PAYLOAD_STATUS_TASK_REMOVED } from "routes/audit/lib/audit-constants.mjs"
import { healTaskAuditStatus } from "routes/schedule/edit/controller/heal-task-audit-status.controller.js"

const AUDIT_TYPE = "customSchedule"
const NO_AUDITS = []
const MAX_HEAL_RETRIES = 3
const HEAL_RETRY_DELAY_MS = 5000

/*
 * Keeps the task-deletion audits for a schedule draft consistent with the tasks present currently
 *
 * Undo and redo remove/restore the schedule tasks, but not the audits.
 * After a task is deleted, a deletion audit is created. then:
 * - after a task is undo-ed back into existence, the corresponding audit is marked as superseded
 * - after a task is redo-ed back out of existence, the corresponding audit is put back (revived) into the live pool
 *
 * exported as component to allow for conditional gating on the hook
 */
export function HealTaskDeletionAudits({ schedule, referenceId, draftId }) {
    useHealTaskDeletionAudits(schedule, referenceId, draftId)
    return null
}

function useHealTaskDeletionAudits(schedule, referenceId, draftId) {
    // whether a request is currently in flight
    const inFlight = useRef(false)

    // automatic retry state for healing task justification audits
    const retryTimer = useRef(null)
    const attempts = useRef(0)
    const [retryTick, setRetryTick] = useState(0)

    // acquire the set of live and superseded audits
    const baseFilter = { "payload.changeType": "task", "payload.idForDraft": draftId }
    const liveAuditParams = [referenceId, AUDIT_TYPE, { ...baseFilter, "payload.status": PAYLOAD_STATUS_TASK_REMOVED }]
    const supersededAuditParams = [
        referenceId,
        AUDIT_TYPE,
        { ...baseFilter, "payload.status": PAYLOAD_STATUS_TASK_REMOVAL_UNDONE },
    ]
    const liveAudits = getAuditsByReferenceIdAndType.useResults(...liveAuditParams) ?? NO_AUDITS
    const supersededAudits = getAuditsByReferenceIdAndType.useResults(...supersededAuditParams) ?? NO_AUDITS

    const pendingDiff = useMemo(getAuditTaskDiff, [schedule, liveAudits, supersededAudits])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(healAuditTaskDiff, [pendingDiff, retryTick]) // including applyHeal dep would cause render loop
    useEffect(() => () => clearTimeout(retryTimer.current), []) // cancel any pending retry on unmount

    // acquires the audits that differ from the current state for both cases (superseding, revival)
    function getAuditTaskDiff() {
        const presentTaskIds = new Set((schedule?.tasks ?? []).map((task) => task.taskId))
        return {
            toSupersede: liveAudits.filter((audit) => presentTaskIds.has(audit.payload?.taskId)), // audits to mark hidden
            toRevive: supersededAudits.filter((audit) => !presentTaskIds.has(audit.payload?.taskId)), // audits to bring back
        }
    }

    function healAuditTaskDiff() {
        const { toSupersede, toRevive } = pendingDiff
        if (inFlight.current || (!toSupersede.length && !toRevive.length)) return
        inFlight.current = true
        applyHeal(toSupersede, toRevive)
    }

    // ===================================================================================================

    // updates the audit rows with the necessary undone removal (superseded) / regular deletion status flag
    function markDeletionAuditPayload(audit, superseding = true) {
        return healTaskAuditStatus({ auditId: audit.id, superseding })
    }

    // extracted from healAuditTaskDiff to allow asynchronous logic outside of callback
    async function applyHeal(toSupersede, toRevive) {
        try {
            const results = await Promise.all([
                ...toSupersede.map((audit) => markDeletionAuditPayload(audit)),
                ...toRevive.map((audit) => markDeletionAuditPayload(audit, false)),
            ])
            if (results.some((ok) => !ok)) throw new Error("One or more audits were not healed")
            attempts.current = 0
            invalidate("getAuditsByReferenceIdAndType") // matches remove-task
        } catch (e) {
            console.warn("Task deletion audit healing failed (offline or network error):", e?.message || e)
            scheduleRetry()
        } finally {
            inFlight.current = false
        }
    }

    // re-runs the heal effect after a growing delay via the dep, capped at MAX_HEAL_RETRIES
    function scheduleRetry() {
        if (attempts.current >= MAX_HEAL_RETRIES) return
        attempts.current++
        clearTimeout(retryTimer.current)
        retryTimer.current = setTimeout(() => setRetryTick((tick) => tick + 1), HEAL_RETRY_DELAY_MS * attempts.current)
    }
}
